import moment from 'moment-timezone';
import { Timestamp } from 'firebase/firestore';
import type { Bootcamp, BootcampState } from '../types/bootcamp';

// Type for serialized Firestore timestamps from Cloud Functions
interface SerializedTimestamp {
  _seconds: number;
  _nanoseconds: number;
}

type TimestampLike = Timestamp | SerializedTimestamp | Date;

/**
 * Convert various timestamp formats to Date
 * Handles: Firestore Timestamp, serialized JSON {_seconds, _nanoseconds}, or Date
 */
const toDate = (timestamp: TimestampLike): Date => {
  if (timestamp instanceof Date) {
    return timestamp;
  }
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  // Serialized timestamp from Cloud Function JSON response
  if ('_seconds' in timestamp) {
    return new Date(timestamp._seconds * 1000);
  }
  // Fallback - try to parse as date
  return new Date(timestamp as unknown as string);
};

/**
 * Determine bootcamp state from dates
 * State is inferred rather than stored to prevent data getting out of sync
 */
export const getBootcampState = (bootcamp: Bootcamp): BootcampState => {
  const now = new Date();
  const regOpen = toDate(bootcamp.dates.registrationOpen);
  const regClose = toDate(bootcamp.dates.registrationClose);
  const eventEnd = toDate(bootcamp.dates.eventEnd);

  if (now < regOpen) return 'upcoming';
  if (now >= regOpen && now <= regClose) return 'registration_open';
  if (now > eventEnd) return 'completed';
  return 'upcoming'; // Between regClose and eventEnd
};

/**
 * Check if early bird pricing is active for a bootcamp
 */
export const isEarlyBirdActive = (bootcamp: Bootcamp): boolean => {
  if (!bootcamp.dates.earlyBirdEnd) return false;
  return new Date() < toDate(bootcamp.dates.earlyBirdEnd);
};

/**
 * Format a time in the bootcamp's local timezone
 * e.g., "1:00 PM" - always in the event's timezone regardless of user's browser
 */
export const formatEventTime = (date: TimestampLike, bootcamp: Bootcamp): string => {
  const d = toDate(date);
  return moment(d).tz(bootcamp.location.timezone).format('h:mm A');
};

/**
 * Format a date in the bootcamp's local timezone
 * e.g., "Friday Oct 17"
 */
export const formatEventDate = (date: TimestampLike, bootcamp: Bootcamp): string => {
  const d = toDate(date);
  return moment(d).tz(bootcamp.location.timezone).format('dddd MMM D');
};

/**
 * Format a short date in the bootcamp's local timezone
 * e.g., "Oct 17"
 */
export const formatEventDateShort = (date: TimestampLike, bootcamp: Bootcamp): string => {
  const d = toDate(date);
  return moment(d).tz(bootcamp.location.timezone).format('MMM D');
};

/**
 * Format a date range in the bootcamp's local timezone
 * e.g., "Oct 17-18, 2025"
 */
export const formatEventDateRange = (
  start: TimestampLike,
  end: TimestampLike,
  bootcamp: Bootcamp
): string => {
  const startDate = toDate(start);
  const endDate = toDate(end);

  const tz = bootcamp.location.timezone;
  const startMoment = moment(startDate).tz(tz);
  const endMoment = moment(endDate).tz(tz);

  // Check if same month
  if (startMoment.month() === endMoment.month()) {
    // Same month: "Oct 17-18, 2025"
    return `${startMoment.format('MMM D')}-${endMoment.format('D, YYYY')}`;
  } else {
    // Different months: "Sep 30 - Oct 2, 2025"
    return `${startMoment.format('MMM D')} - ${endMoment.format('MMM D, YYYY')}`;
  }
};

/**
 * Format a full datetime in the bootcamp's local timezone
 * e.g., "Friday, October 17, 2025 at 1:00 PM"
 */
export const formatEventDateTime = (date: TimestampLike, bootcamp: Bootcamp): string => {
  const d = toDate(date);
  return moment(d).tz(bootcamp.location.timezone).format('dddd, MMMM D, YYYY [at] h:mm A');
};

/**
 * Get the timezone abbreviation for display
 * e.g., "ET", "CT", "PT"
 */
export const getTimezoneAbbr = (bootcamp: Bootcamp): string => {
  return moment().tz(bootcamp.location.timezone).format('z');
};

/**
 * Format price for display
 * $0 = "FREE", otherwise "$X" or "Early Bird: $Y"
 */
export const formatPriceDisplay = (
  amount: number,
  earlyBirdAmount?: number,
  isEarlyBird?: boolean
): string => {
  if (amount === 0) return 'FREE';
  if (isEarlyBird && earlyBirdAmount !== undefined) {
    return `$${earlyBirdAmount} (Early Bird)`;
  }
  return `$${amount}`;
};

/**
 * Get the Bookeo slot date range from bootcamp dates
 * Returns ISO strings for startTime and endTime
 * Range is day before eventStart to day after eventEnd
 */
export const getBookeoSlotRange = (
  bootcamp: Bootcamp
): { startTime: string; endTime: string } => {
  const tz = bootcamp.location.timezone;
  const startTime = moment(toDate(bootcamp.dates.eventStart))
    .tz(tz)
    .subtract(1, 'day')
    .startOf('day')
    .toISOString();
  const endTime = moment(toDate(bootcamp.dates.eventEnd))
    .tz(tz)
    .add(1, 'day')
    .endOf('day')
    .toISOString();

  return { startTime, endTime };
};
